import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';
import { TourSection } from './welcomeTourData';

interface WelcomeTourContentProps {
  section: TourSection;
}

const WelcomeTourContent: FC<WelcomeTourContentProps> = ({ section }) => {
  const { t } = useTranslation('welcomeTour');
  const Icon = section.icon;

  return (
    <div className="flex-1 overflow-y-auto p-5">
      {/* Video area */}
      <div className="relative rounded-lg bg-zinc-100 dark:bg-zinc-800 h-80 flex items-center justify-center mb-4 border border-zinc-200 dark:border-zinc-700 overflow-hidden">
        {section.videoUrl ? (
          <video
            src={section.videoUrl}
            controls
            className="w-full h-full rounded-lg object-cover"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-zinc-400 dark:text-zinc-500">
            <div className="rounded-full bg-white dark:bg-zinc-700 p-3 shadow-sm">
              <Play className="h-6 w-6 text-blue-500" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-zinc-600 dark:text-zinc-300">
                {t(section.titleKey)} Demo
              </p>
              <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                {t('TOUR_VIDEO_COMING_SOON')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Feature overview */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/30 p-2 shrink-0">
            <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              {t(section.titleKey)}
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {t(section.subtitleKey)}
            </p>
          </div>
        </div>

        <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed mb-3">
          {t(section.descriptionKey)}
        </p>

        {/* Bullet points in a 2-column grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {section.bullets.map((bullet, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">
                {t(bullet.textKey)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WelcomeTourContent;
